import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';
// import { mockLogin, mockRegister, mockLogout, mockCheckAuth, mockAuthState } from '../utils/mock';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check authentication status on mount
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const userData = await authService.getCurrentUser();
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem('authToken');
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const data = await authService.login(email, password);
      localStorage.setItem('authToken', data.access_token);

      const userData = await authService.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true, user: userData };
    } catch (error) {
      // Handle different error types
      if (error.response) {
        // Server responded with error
        const errorDetail = error.response?.data?.detail || 'Login failed';
        return { success: false, message: errorDetail };
      } else if (error.message) {
        // Network error or timeout
        return { success: false, message: error.message };
      } else {
        return { success: false, message: 'Login failed. Please try again.' };
      }
    }
  };

  const register = async (userData) => {
    try {
      const data = await authService.register(userData);
      // New API doesn't return tokens - must verify email first
      return {
        success: true,
        message: data.message || 'Registration successful! Please verify your email.',
        email: data.email
      };
    } catch (error) {
      // Handle different error types
      if (error.response) {
        // Server responded with error
        const errorDetail = error.response?.data?.detail || 'Registration failed';
        return { success: false, message: errorDetail };
      } else if (error.message) {
        // Network error or timeout
        return { success: false, message: error.message };
      } else {
        return { success: false, message: 'Registration failed. Please try again.' };
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = async (updatedData) => {
    try {
      const updatedUser = await authService.updateUser(updatedData);
      setUser(updatedUser);
      return { success: true };
    } catch (error) {
      console.error("Failed to update user:", error);
      return { success: false, message: error.response?.data?.detail || 'Update failed' };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};